import React, { useState, useMemo } from 'react';

// Simple deterministic pseudo-random number generator
function mulberry32(a) {
  return function() {
    var t = a += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}

function stringToSeed(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

const FIRST_NAMES = ["James", "John", "Robert", "Michael", "William", "David", "Richard", "Charles", "Joseph", "Thomas", "Christopher", "Daniel", "Paul", "Mark", "Donald", "George", "Kenneth", "Steven", "Edward", "Brian", "Ronald", "Anthony", "Kevin", "Jason", "Matthew", "Gary", "Timothy", "Jose", "Larry", "Jeffrey", "Frank", "Scott", "Eric", "Stephen", "Andrew", "Raymond", "Gregory", "Joshua", "Jerry", "Dennis", "Walter", "Patrick", "Peter", "Harold", "Douglas", "Henry", "Carl", "Arthur", "Ryan", "Roger", "Joe", "Juan", "Jack", "Albert", "Jonathan", "Justin", "Terry", "Gerald", "Keith", "Samuel", "Ralph", "Lawrence", "Nicholas", "Roy", "Benjamin", "Bruce", "Brandon", "Adam", "Harry", "Fred", "Wayne", "Billy", "Steve", "Louis", "Jeremy", "Aaron", "Randy", "Howard", "Eugene", "Carlos", "Russell", "Bobby", "Victor", "Martin", "Ernest", "Phillip", "Todd", "Jesse", "Craig", "Alan", "Shawn", "Clarence", "Sean", "Philip", "Chris", "Johnny", "Earl", "Jimmy", "Antonio", "Tanya", "Aarav", "Priya", "Rahul", "Neha", "Vikram", "Sneha", "Rohan", "Ananya", "Amit", "Elena", "Dmitry", "Olga", "Ivan", "Sofia", "Yuki", "Hiroshi", "Sakura", "Kenji", "Mei", "Wei", "Jian", "Li", "Hua", "Min"];
const LAST_INITIALS = ["A.", "B.", "C.", "D.", "E.", "F.", "G.", "H.", "I.", "J.", "K.", "L.", "M.", "N.", "O.", "P.", "R.", "S.", "T.", "V.", "W.", "Y.", "Z."];
const FLAGS = ["🇺🇸", "🇬🇧", "🇨🇦", "🇦🇺", "🇩🇪", "🇫🇷", "🇮🇳", "🇪🇸", "🇮🇹", "🇧🇷", "🇯🇵", "🇿🇦", "🇲🇽"];

const REVIEW_TEXTS_5 = [
  "Passed my prop firm challenge with this! Incredible precision and low drawdown.",
  "Solid EA, great support from the developer. Highly recommend.",
  "I've been running this for 3 months now on a live account. Very stable growth.",
  "Exactly what I was looking for. The built-in news filter saves me during high-impact events.",
  "Good performance so far. I have already recommended it to two of my trading friends.",
  "Excellent strategy. I tweaked the lot sizing a bit and it's producing consistent daily profits.",
  "Probably the best robot I've purchased this year. Customer service is also top notch.",
  "Works flawlessly on my ECN broker. Just set it and forget it.",
  "Insane win rate. It doesn't trade every day, but when it does, it's highly accurate.",
  "Very impressed. The documentation was clear and setup took only 5 minutes."
];

const REVIEW_TEXTS_4 = [
  "Has its pros and cons. Works well on trending pairs but struggles a bit during sideways action.",
  "Good EA, but requires a VPS with very low latency to get the best entries.",
  "Profitable overall. Drawdown is slightly higher than expected but it recovers quickly.",
  "Solid results so far. Would be 5 stars if the documentation was a bit more detailed.",
  "I like the strategy. It takes small profits consistently. Occasionally hits a stop loss but manages risk well.",
  "Works well, but you need to be careful with the lot size parameter."
];

const REVIEW_TEXTS_3 = [
  "It's okay. Made some profit, hit some stop losses. Breaking even right now.",
  "Requires a lot of optimization to get the backtest results. Out of the box it's average.",
  "Decent EA, but not trading as frequently as I hoped."
];

const REVIEW_TEXTS_2 = [
  "Not very impressed. The drawdown was much deeper than the backtests suggested.",
  "It opened too many trades during a news event and I took a big loss. Needs better filtering.",
  "Struggles significantly in ranging markets. You have to turn it off frequently."
];

const REVIEW_TEXTS_1 = [
  "Terrible experience. Blew a significant portion of my account despite following the manual.",
  "Does not match the backtest results at all. Do not run this on a live account without serious forward testing.",
  "Complete waste of money. The strategy is fundamentally flawed."
];

const FakeReviews = ({ productId, productName }) => {
  const [sortBy, setSortBy] = useState('Newest Reviews');

  const { reviews, stats } = useMemo(() => {
    const seed = stringToSeed(productId || "default") + 12345;
    const random = mulberry32(seed);

    const numReviews = Math.floor(random() * 110) + 40;
    
    let totalScore = 0;
    const starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const generatedReviews = [];

    const now = new Date('2026-06-25T00:00:00Z').getTime(); 

    for (let i = 0; i < numReviews; i++) {
      const randStar = random();
      let stars = 5;
      let textPool = REVIEW_TEXTS_5;
      
      if (randStar > 0.97) {
        stars = 1;
        textPool = REVIEW_TEXTS_1;
      } else if (randStar > 0.93) {
        stars = 2;
        textPool = REVIEW_TEXTS_2;
      } else if (randStar > 0.85) {
        stars = 3;
        textPool = REVIEW_TEXTS_3;
      } else if (randStar > 0.60) {
        stars = 4;
        textPool = REVIEW_TEXTS_4;
      }
      
      starCounts[stars]++;
      totalScore += stars;

      const firstName = FIRST_NAMES[Math.floor(random() * FIRST_NAMES.length)];
      const lastInit = LAST_INITIALS[Math.floor(random() * LAST_INITIALS.length)];
      const flag = FLAGS[Math.floor(random() * FLAGS.length)];
      
      let text = textPool[Math.floor(random() * textPool.length)];
      if (random() > 0.7 && stars >= 4 && productName) {
        text += ` The EA itself is called ${productName} for context.`;
      }

      const daysAgo = Math.floor(random() * 180);
      const date = new Date(now - (daysAgo * 24 * 60 * 60 * 1000));
      const formattedDate = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      generatedReviews.push({
        id: `rev-${i}`,
        name: `${firstName} ${lastInit}`,
        initials: `${firstName[0]}${lastInit[0]}`,
        date: formattedDate,
        timestamp: date.getTime(),
        stars,
        text,
        flag
      });
    }

    const average = (totalScore / numReviews).toFixed(1);

    return { 
      reviews: generatedReviews.sort((a, b) => b.timestamp - a.timestamp), 
      stats: { total: numReviews, average, starCounts } 
    };
  }, [productId, productName]);

  const displayReviews = useMemo(() => {
    let sorted = [...reviews];
    if (sortBy === 'Highest Rating') {
      sorted.sort((a, b) => b.stars - a.stars);
    } else if (sortBy === 'Lowest Rating') {
      sorted.sort((a, b) => a.stars - b.stars);
    }
    return sorted;
  }, [reviews, sortBy]);

  return (
    <div className="w-full">
      <div className="glass-panel p-8 rounded-xl border border-white/5 mb-8 flex flex-col md:flex-row gap-8 items-center">
        <div className="md:w-1/3 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-white/10 pb-6 md:pb-0 md:pr-6">
          <div className="text-[64px] font-bold text-white leading-none mb-2">{stats.average}</div>
          <div className="text-on-surface-variant text-[14px] mb-2">out of 5 stars</div>
          <div className="flex gap-1 text-primary mb-3">
            {[1, 2, 3, 4, 5].map(star => (
              <span key={star} className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: star <= Math.round(stats.average) ? "'FILL' 1" : "'FILL' 0" }}>
                star
              </span>
            ))}
          </div>
          <div className="text-on-surface-variant text-[12px]">Based on {stats.total} verified reviews</div>
        </div>
        
        <div className="md:w-2/3 w-full space-y-3">
          {[5, 4, 3, 2, 1].map(star => {
            const percentage = Math.round((stats.starCounts[star] / stats.total) * 100);
            return (
              <div key={star} className="flex items-center gap-4 text-[13px] text-on-surface-variant">
                <div className="w-12 whitespace-nowrap">{star} Star</div>
                <div className="flex-1 h-2 bg-[#1A1B20] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className="w-10 text-right">{percentage}%</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-white font-bold text-[18px]">Customer Reviews ({stats.total})</h3>
        <div className="flex items-center gap-3">
          <span className="text-on-surface-variant text-[13px]">Sort by:</span>
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[#1A1B20] border border-white/10 text-white text-[13px] rounded-lg px-4 py-2 focus:outline-none focus:border-primary"
          >
            <option>Newest Reviews</option>
            <option>Highest Rating</option>
            <option>Lowest Rating</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {displayReviews.map(review => (
          <div key={review.id} className="glass-panel p-6 rounded-xl border border-white/5">
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 text-primary border border-primary/30 flex items-center justify-center font-bold text-[14px] shrink-0">
                  {review.initials}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-bold text-[14px]">{review.name}</span>
                    <span className="text-[16px] leading-none">{review.flag}</span>
                  </div>
                  <div className="text-on-surface-variant text-[12px]">{review.date}</div>
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-2">
                <div className="flex gap-0.5 text-primary">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: star <= review.stars ? "'FILL' 1" : "'FILL' 0" }}>
                      star
                    </span>
                  ))}
                </div>
                <div className="bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 px-2 py-0.5 rounded flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase">
                  <span className="material-symbols-outlined text-[12px]">verified</span>
                  Verified Purchase
                </div>
              </div>
            </div>
            <p className="text-on-surface-variant text-[14px] leading-relaxed">
              {review.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FakeReviews;
